import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIContext } from '../../Contexts/APIContext';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
}

type Option = { id: string; label: string };
type VersionOption = { id: number; label: string; isCurrent: boolean };
type SubLandUseOption = Option & { mainLandUse: string };
type CreateSiteOptions = {
    versions: VersionOption[];
    areas: Option[];
    mainLandUses: Option[];
    subLandUses: SubLandUseOption[];
};
type CreateSiteResult = {
    reference: string;
    versionId: number;
    versionName: string;
    hasTravelPlan: boolean;
};

export default function CreateSiteDialog({ open, onClose, onCreated }: Props) {
    const { get, post, setVersion, version } = useContext(APIContext);
    const navigate = useNavigate();
    const [options, setOptions] = useState<CreateSiteOptions | null>(null);
    const [versionId, setVersionId] = useState(0);
    const [area, setArea] = useState<Option | null>(null);
    const [mainLandUse, setMainLandUse] = useState<Option | null>(null);
    const [subLandUse, setSubLandUse] = useState<SubLandUseOption | null>(null);
    const [isMultiModal, setIsMultiModal] = useState(false);
    const [description, setDescription] = useState('');
    const [includeTravelPlan, setIncludeTravelPlan] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<CreateSiteResult | null>(null);

    useEffect(() => {
        if (!open) return;

        setOptions(null);
        setVersionId(0);
        setArea(null);
        setMainLandUse(null);
        setSubLandUse(null);
        setIsMultiModal(false);
        setDescription('');
        setIncludeTravelPlan(false);
        setSubmitting(false);
        setResult(null);

        get('Admin', 'GetCreateSiteOptions', (items: CreateSiteOptions) => {
            setOptions(items);
            const selectedVersion = items.versions.find(item => item.id === version)
                ?? items.versions[0];
            setVersionId(selectedVersion?.id ?? 0);
        });
    }, [open]);

    const subLandUses = useMemo(
        () => options?.subLandUses.filter(item => item.mainLandUse === mainLandUse?.id) ?? [],
        [options, mainLandUse]
    );

    const canSubmit = versionId > 0 && area !== null && mainLandUse !== null && subLandUse !== null && !submitting;

    const createSite = async () => {
        if (!canSubmit || !area || !mainLandUse || !subLandUse) return;

        setSubmitting(true);
        await post('Admin', 'CreateSite', {
            versionId,
            areaCode: area.id,
            mainLandUse: mainLandUse.id,
            subLandUse: subLandUse.id,
            isMultiModal,
            description: description.trim() || null,
            includeTravelPlan
        }, (createdSite: CreateSiteResult) => {
            setResult(createdSite);
            onCreated?.();
        });
        setSubmitting(false);
    };

    const openSite = () => {
        if (!result) return;
        setVersion(result.versionId);
        onClose();
        const params = new URLSearchParams({
            siteRef: result.reference,
            returnTo: '/admin/',
            returnLabel: 'Back to Admin'
        });
        navigate(`/site?${params.toString()}`);
    };

    return <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
        <MaintenanceDialogTitle
            icon={<AddBusinessOutlinedIcon />}
            title="Create a site"
            description="Add a new site to a selected data version"
        />
        <DialogContent>
            {result ? <Typography sx={{ mt: 1 }}>
                Site {result.reference} was created.
            </Typography> : <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                    select
                    required
                    fullWidth
                    label="Data version"
                    value={versionId}
                    disabled={!options}
                    onChange={event => setVersionId(Number(event.target.value))}
                >
                    {options?.versions.map(item => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                </TextField>

                <Autocomplete
                    options={options?.areas ?? []}
                    value={area}
                    disabled={!options}
                    onChange={(_, value) => setArea(value)}
                    getOptionLabel={item => item.label}
                    isOptionEqualToValue={(item, value) => item.id === value.id}
                    renderInput={params => <TextField {...params} required label="Area" />}
                />

                <Autocomplete
                    options={options?.mainLandUses ?? []}
                    value={mainLandUse}
                    disabled={!options}
                    onChange={(_, value) => {
                        setMainLandUse(value);
                        setSubLandUse(null);
                    }}
                    getOptionLabel={item => item.label}
                    isOptionEqualToValue={(item, value) => item.id === value.id}
                    renderInput={params => <TextField {...params} required label="Main land use" />}
                />

                <Autocomplete
                    options={subLandUses}
                    value={subLandUse}
                    disabled={!mainLandUse}
                    onChange={(_, value) => setSubLandUse(value)}
                    getOptionLabel={item => item.label}
                    isOptionEqualToValue={(item, value) => item.id === value.id && item.mainLandUse === value.mainLandUse}
                    renderInput={params => <TextField {...params} required label="Sub land use" />}
                />

                <TextField
                    select
                    required
                    fullWidth
                    label="Survey coverage"
                    value={isMultiModal ? 'multiModal' : 'vehicles'}
                    onChange={event => setIsMultiModal(event.target.value === 'multiModal')}
                >
                    <MenuItem value="vehicles">Vehicles only</MenuItem>
                    <MenuItem value="multiModal">Multi-modal</MenuItem>
                </TextField>

                <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={event => setDescription(event.target.value)}
                />

                <FormControlLabel
                    control={<Checkbox checked={includeTravelPlan} onChange={event => setIncludeTravelPlan(event.target.checked)} />}
                    label="Add Travel Plan"
                />
            </Stack>}
        </DialogContent>
        <DialogActions>
            {result ? <>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" onClick={openSite}>Open Site</Button>
            </> : <>
                <Button onClick={onClose} disabled={submitting}>Close</Button>
                <Button variant="contained" onClick={createSite} disabled={!canSubmit}>Create Site</Button>
            </>}
        </DialogActions>
    </Dialog>;
}
